import { useQuery, useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";
import {
	AlertCircle,
	BookOpenIcon,
	ChevronRightIcon,
	Loader2,
	PlusCircleIcon,
	SearchIcon,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import {
	Filter,
	FilterContent,
	FilterItem,
	FilterTrigger,
} from "@/components/admin/Filter";
import TableHeadSortButton from "@/components/admin/TableHeadSortButton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import Container from "@/components/ui/container";
import {
	Table,
	TableBody,
	TableCaption,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import cacheKeys from "@/config/cache-keys";
import pages from "@/config/pages";
import { useDebugFlags } from "@/hooks/useDebugFlags";
import AppService from "@/services/AppService";
import type { APIResponse } from "@/types/APIresponse";
import type { Account } from "@/types/account/account";
import { AccountStatus } from "@/types/account/account";
import type { GValue } from "@/types/gValue/gValue";

const appService = new AppService();

type SortState = {
	field:
		| "name"
		| "main_account_display_name"
		| "groups"
		| "email"
		| "phone_nr"
		| "status";
	direction: "asc" | "desc" | "none";
};

const DashboardPage = () => {
	const [selectedNames, setSelectedNames] = useState(new Set<string>());
	const productCatalogueNavVisible = useDebugFlags(
		(s) => s.productCatalogueNavVisible,
	);
	const [selectedStatus, setSelectedStatus] = useState(new Set<string>());

	const [sortState, setSortState] = useState<SortState>({
		field: "name",
		direction: "asc",
	});

	const navigate = useNavigate();
	const queryClient = useQueryClient();

	// Fetch GValue
	const { data: gValueData, isLoading: isGValueLoading } = useQuery<
		APIResponse<GValue[]>,
		Error
	>({
		queryKey: cacheKeys.GVALUE,
		queryFn: appService.getValues,
	});
	const latestGValue = gValueData?.Result?.[0]?.value || "N/A";
	// if (isGValueLoading) {
	// } else {
	// }
	// if (isGValueError) {
	// 	console.error(
	// 		"DashboardPage: Error fetching GValue:",
	// 		gValueError?.message
	// 	);
	// }

	// API Data Fetching
	const {
		data: apiResponse,
		isLoading,
		isError,
		error,
		isRefetching,
	} = useQuery<APIResponse<Account[]>, Error>({
		queryKey: ["accounts", "all"],
		queryFn: () => {
			return appService.getAllAccounts();
		},
	});

	const accountsData: Account[] = useMemo(
		() => apiResponse?.Result ?? [],
		[apiResponse],
	);

	const optionsName = useMemo(
		() =>
			accountsData
				.map((acc) => ({
					value: acc.display_name,
					label: acc.display_name,
				}))
				.filter(
					(
						value,
						index,
						self, // Filter out duplicates based on label
					) => index === self.findIndex((t) => t.label === value.label),
				),
		[accountsData],
	);
	// Get all unique names from accountsData
	const optionsStatus = Object.values(AccountStatus).map((status) => ({
		label: status.charAt(0).toUpperCase() + status.slice(1),
		value: status,
	}));

	// Filter Toggle Functions
	const toggleNames = (value: string) => {
		const newValues = new Set(selectedNames);
		if (newValues.has(value)) {
			newValues.delete(value);
		} else {
			newValues.add(value);
		}
		setSelectedNames(newValues);
	};

	const toggleStatus = (value: string) => {
		const newValues = new Set(selectedStatus);
		if (newValues.has(value)) {
			newValues.delete(value);
		} else {
			newValues.add(value);
		}
		setSelectedStatus(newValues);
	};

	// Filtering and Sorting
	const filteredAndSortedAccounts = useMemo(() => {
		const filtered = accountsData.filter((acc) => {
			const nameMatch =
				selectedNames.size === 0 || selectedNames.has(acc.display_name);
			const statusMatch =
				selectedStatus.size === 0 || selectedStatus.has(acc.status);
			return nameMatch && statusMatch;
		});

		const sorted = filtered.sort((a, b) => {
			if (sortState.field === "name") {
				return sortState.direction === "asc"
					? a.display_name.localeCompare(b.display_name)
					: b.display_name.localeCompare(a.display_name);
			} else if (sortState.field === "main_account_display_name") {
				return sortState.direction === "asc"
					? a.main_account_display_name.localeCompare(
							b.main_account_display_name,
						)
					: b.main_account_display_name.localeCompare(
							a.main_account_display_name,
						);
			} else if (sortState.field === "groups") {
				return sortState.direction === "asc"
					? (a.groups?.length || 0) - (b.groups?.length || 0)
					: (b.groups?.length || 0) - (a.groups?.length || 0);
			} else if (sortState.field === "email") {
				return sortState.direction === "asc"
					? a.email.localeCompare(b.email)
					: b.email.localeCompare(a.email);
			} else if (sortState.field === "phone_nr") {
				return sortState.direction === "asc"
					? a.phone_nr.localeCompare(b.phone_nr)
					: b.phone_nr.localeCompare(a.phone_nr);
			} else if (sortState.field === "status") {
				return sortState.direction === "asc"
					? a.status.localeCompare(b.status)
					: b.status.localeCompare(a.status);
			}
			return 0;
		});
		return sorted;
	}, [sortState, accountsData, selectedNames, selectedStatus]);

	// Loading State
	if (isLoading) {
		return (
			<Container className="flex justify-center items-center py-20">
				<Loader2 className="h-8 w-8 animate-spin text-howden-blue" />
				<span className="ml-2">Loading Accounts...</span>
			</Container>
		);
	}
	if (isError) {
		const backendMessage =
			apiResponse?.LocalizedMessage || apiResponse?.Message;
		const errorMessage =
			backendMessage ||
			(error instanceof Error ? error.message : "En ukjent feil oppstod");
		console.error("DashboardPage: Displaying error UI -", errorMessage);

		return (
			<Container className="py-10">
				<Card className="border-destructive bg-destructive/10">
					<CardHeader className="flex flex-row items-center space-x-2">
						<AlertCircle className="h-5 w-5 text-destructive" />
						<CardTitle className="text-destructive">
							Feil ved henting av kontoer
						</CardTitle>
					</CardHeader>
					<CardContent>
						<p>Kunne ikke laste inn kontodata fra serveren.</p>
						<p className="text-sm text-muted-foreground mt-2">
							Detaljer: {errorMessage}
						</p>
						<Button
							variant="outline"
							size="sm"
							className="mt-4"
							onClick={() => {
								queryClient.invalidateQueries({
									queryKey: ["accounts", "all"],
								});
							}}
						>
							Prøv igjen
						</Button>
					</CardContent>
				</Card>
			</Container>
		);
	}

	return (
		<div className="bg-soft-white dark:bg-[#121212]">
			<Container className="mx-auto px-4 py-6">
				<div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
					{/* Current G Value Card - Clickable */}
					<Card
						onClick={() => {
							navigate(pages.GValuePage);
						}}
						className="cursor-pointer hover:shadow transition"
					>
						<CardHeader>
							<CardTitle className="text-sm text-howden-grey">
								{t("admin.dashboard.gvalue")}
							</CardTitle>
						</CardHeader>
						<CardContent>
							<div className="flex items-center justify-between">
								{/* Display the latest GValue */}
								{isGValueLoading ? (
									<span className="text-sm text-muted-foreground">
										Loading...
									</span>
								) : isError ? (
									<span className="text-sm text-destructive">Error</span>
								) : (
									<span className="text-2xl font-bold">
										NOK{" "}
										{typeof latestGValue === "number"
											? latestGValue.toLocaleString("nb-NO")
											: latestGValue}
									</span>
								)}
								<ChevronRightIcon className="h-5 w-5" />
							</div>
						</CardContent>
					</Card>

					{/* !TODO Vi kan se an om vi får tid til i legge inn logikken her */}
					{/* New Clients this month */}
					<Card className="border transition">
						<CardHeader>
							<CardTitle className="text-sm text-howden-grey">
								{t("admin.dashboard.this-month")}
							</CardTitle>
						</CardHeader>
						<CardContent>
							<span className="text-2xl font-bold"> - </span>
						</CardContent>
					</Card>

					{/* Active Clients */}
					<Card className="border transition">
						<CardHeader>
							<CardTitle className="text-sm text-howden-grey">
								{t("admin.dashboard.active-accounts")}
							</CardTitle>
						</CardHeader>
						<CardContent>
							<span className="text-2xl font-bold">
								{
									accountsData.filter(
										(acc) => acc.status === AccountStatus.Active,
									).length
								}
							</span>
						</CardContent>
					</Card>
				</div>
				{/* Filter and Create New Account Button */}
				<div className="flex items-center justify-between">
					<div className="flex gap-2">
						<Button
							onClick={() => {
								navigate(pages.AdminMainAccountCreate);
							}}
						>
							<PlusCircleIcon />
							{t("admin.dashboard.create-button")}
						</Button>
						{productCatalogueNavVisible ? (
							<Button
								variant="outline"
								onClick={() => navigate(pages.AdminProductDefinitions)}
							>
								<BookOpenIcon />
								{t("admin.dashboard.product-definitions-button")}
							</Button>
						) : null}
						<Filter>
							<FilterTrigger
								name="Accounts"
								icon={<SearchIcon />}
								selectedValues={selectedNames}
								options={optionsName}
							/>
							<FilterContent
								className="ml-8"
								selectedSize={selectedNames.size}
								clearSelected={() => setSelectedNames(new Set())}
							>
								{optionsName.map((option) => (
									<FilterItem
										key={option.value}
										option={option}
										isSelected={selectedNames.has(option.value)}
										toggleValue={toggleNames}
									/>
								))}
							</FilterContent>
						</Filter>
						<Filter>
							<FilterTrigger
								name="Status"
								selectedValues={selectedStatus}
								options={optionsStatus}
							/>
							<FilterContent
								className="ml-8"
								selectedSize={selectedStatus.size}
								clearSelected={() => setSelectedStatus(new Set())}
							>
								{optionsStatus.map((option) => (
									<FilterItem
										key={option.value}
										option={option}
										isSelected={selectedStatus.has(option.value)}
										toggleValue={toggleStatus}
									/>
								))}
							</FilterContent>
						</Filter>
					</div>

					{/* Refetch Button and Count */}
					<div>
						{isRefetching && <Loader2 className="ml-2 h-4 w-4 animate-spin" />}
						<Badge variant="secondary" className="text-sm rounded">
							{accountsData.length} {t("admin.dashboard.row")}
						</Badge>
					</div>
				</div>

				{/* Table */}
				<Table>
					<TableCaption>{t("admin.table.caption")}</TableCaption>
					<TableHeader>
						<TableRow className="hover:bg-inherit">
							<TableHead>
								<TableHeadSortButton
									label="Main Account"
									sortDir={
										sortState.field == "main_account_display_name"
											? sortState.direction
											: "none"
									}
									setSortDir={(dir) =>
										setSortState((prev) => ({
											...prev,
											field: "main_account_display_name",
											direction: dir,
										}))
									}
								/>
							</TableHead>
							<TableHead>
								<TableHeadSortButton
									label="Account"
									sortDir={
										sortState.field == "name" ? sortState.direction : "none"
									}
									setSortDir={(dir) =>
										setSortState((prev) => ({
											...prev,
											field: "name",
											direction: dir,
										}))
									}
								/>
							</TableHead>
							<TableHead>
								<TableHeadSortButton
									label={t("admin.table.groups")}
									sortDir={
										sortState.field == "groups" ? sortState.direction : "none"
									}
									setSortDir={(dir) =>
										setSortState((prev) => ({
											...prev,
											field: "groups",
											direction: dir,
										}))
									}
								/>
							</TableHead>
							<TableHead>
								<TableHeadSortButton
									label={t("admin.table.email")}
									sortDir={
										sortState.field == "email" ? sortState.direction : "none"
									}
									setSortDir={(dir) =>
										setSortState((prev) => ({
											...prev,
											field: "email",
											direction: dir,
										}))
									}
								/>
							</TableHead>
							<TableHead>
								<TableHeadSortButton
									label={t("admin.table.phone")}
									sortDir={
										sortState.field == "phone_nr" ? sortState.direction : "none"
									}
									setSortDir={(dir) =>
										setSortState((prev) => ({
											...prev,
											field: "phone_nr",
											direction: dir,
										}))
									}
								/>
							</TableHead>
							<TableHead>
								<TableHeadSortButton
									label="Status"
									sortDir={
										sortState.field == "status" ? sortState.direction : "none"
									}
									setSortDir={(dir) =>
										setSortState((prev) => ({
											...prev,
											field: "status",
											direction: dir,
										}))
									}
								/>
							</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{/* checks if there is accounts */}
						{filteredAndSortedAccounts.length === 0 ? (
							<TableRow>
								<TableCell
									colSpan={6}
									className="h-24 text-center text-muted-foreground"
								>
									{" "}
									{t("admin.table.message.no-accounts")}{" "}
									{accountsData.length > 0 ? "med gjeldende filtre." : "."}
								</TableCell>
							</TableRow>
						) : (
							filteredAndSortedAccounts.map(
								(
									account, // Map over filtered/sorted data
								) => (
									<TableRow
										key={account.id}
										className="hover:bg-white dark:hover:bg-black/10"
									>
										{/* Main Account */}
										<TableCell>
											<Button
												variant="link"
												className="dark:text-soft-white"
												onClick={() =>
													navigate(
														pages.AdminMainAccountDetails(
															account.main_account_id,
														),
													)
												}
												title={`Gå til Main Account: ${account.main_account_id}`}
											>
												{account.main_account_display_name}
											</Button>
										</TableCell>

										{/* The Specific Account) */}
										<TableCell>
											<Button
												variant="link"
												className="dark:text-soft-white"
												onClick={() =>
													navigate(
														pages.AdminAccount(
															account.main_account_id, // Pass main account ID
															account.id, // Pass this specific account's ID
														),
													)
												}
											>
												{account.display_name}
											</Button>
										</TableCell>

										{/* Subgroups */}
										<TableCell className="gap-2 px-8">
											{/* SAFE ACCESS: nullish coalescing */}
											{(account.groups ?? []).length} gruppe
											{(account.groups ?? []).length !== 1 ? "r" : ""}
										</TableCell>
										<TableCell className="gap-2 px-8">
											{account.email}
										</TableCell>
										<TableCell className="gap-2 px-8">
											{account.phone_nr}
										</TableCell>
										{/* Status Badge */}
										<TableCell>
											<Badge
												variant={
													account.status === AccountStatus.Active
														? "default"
														: account.status === AccountStatus.Suspended
															? "destructive"
															: account.status === AccountStatus.Inactive
																? "secondary"
																: "outline"
												}
											>
												{account.status}
											</Badge>
										</TableCell>
									</TableRow>
								),
							)
						)}
					</TableBody>
				</Table>
			</Container>
		</div>
	);
};

export default DashboardPage;
